import {
  Button,
  Checkbox,
  Flags,
  Icons,
  Select,
  SingleDatePicker,
  Sizes,
  TextInput,
  type IFlagProps,
} from '@sede-enterprise/shell-ds-react-framework';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import Loader from 'src/UI/Loader';
import { sendNotification } from 'src/UI/Notification';
import { CET_TIME_ZONE, COMMON_MESSAGES, NUMBERS } from 'src/constants/Common';
import {
  GET_CONTRACT_BY_PERIOD,
  GET_JOBS,
  GET_MARKET_POSITION_DEALS,
  SEARCH_CONTRACT_BY_PERIOD,
  TIME_SERIES_VIEW_IDS,
} from 'src/constants/EUMarkets';
import { get } from 'src/services/GenesisApi';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { COUNTRY_HEALTH_DATA } from 'src/components/EUMarkets/Dashboard/HealthCheck/constants';
import {
  clear,
  mergeNodes,
  setExpandedKeys,
  setPositionDeals,
} from 'src/store/reducers/ContractViewNew';
import type { IMarketPositionDeal, IMarketPositionNode } from 'src/types/EUMarkets.type';
import { ActionMenu, ContractViewNewContainer, SearchValidationModal } from './style';
import { Jobs, type JobRecord } from '../MarketPositionNew/Jobs';
import CustomViewSelect from '../CustomViewSelect';
import MarketButtonGroup from '../MarketButtonGroup';
import TableFocusToggle from '../TableFocusToggle';
import PositionSearchFilters from '../PositionSearchFilters';
import useCustomViewSelection from '../useCustomViewSelection';
import {
  DEFAULT_PERIOD,
  PERIOD_OPTIONS,
  getDealColumnValue,
  getPeriodColumns,
  getPeriodEndpoint,
  getPeriodRange,
  type PeriodColumn,
  type PositionPeriod,
} from '../period';

dayjs.extend(utc);
dayjs.extend(timezone);

const ROOT_NODE_ID = NUMBERS.ONE;
const CONTRACT_NODE_ID = NUMBERS.FIVE;
const POSITION_NODE_ID = NUMBERS.SIX;
const SHIPPER_PAIR_NODE_ID = NUMBERS.EIGHT;
const TREE_INDENT_STEP_PX = NUMBERS.TWENTY;
const DEAL_INDENT_PX = 140;
const LOADING_TOGGLE_OFFSET_PX = NUMBERS.TWENTY_SIX;
// Tree, volume type and total columns; period value columns are added at runtime.
const FIXED_COLUMN_COUNT = NUMBERS.THREE;
const ZERO = NUMBERS.ZERO;
const REFRESH_ICON_SIZE = 24;
const SEARCH_ICON_SIZE = 24;
const BUTTON_MARGIN_TOP = '25px';
const CALC_VOLUME_TYPE = 'CALC';
const MIN_SEARCH_CHARACTERS = 3;
const SEARCH_VALIDATION_MESSAGE = 'Please enter atleast 3 characters to perform search.';
const LEVEL_AFTER_NODE: Record<number, number | undefined> = {
  [ROOT_NODE_ID]: CONTRACT_NODE_ID,
  [CONTRACT_NODE_ID]: POSITION_NODE_ID,
  [POSITION_NODE_ID]: SHIPPER_PAIR_NODE_ID,
};

const getNodeKey = (node: Pick<IMarketPositionNode, 'nodeId' | 'sourceId'>) =>
  `${node.nodeId}:${node.sourceId}`;

// Auto-expand every ancestor of a name match so users see results without manual expanding.
const computeSearchExpandedKeys = (data: IMarketPositionNode[], searchText: string) => {
  const lowerSearchText = searchText.toLowerCase();
  const matchedNodes = data.filter((node) => node.name?.toLowerCase().includes(lowerSearchText));
  const expandedKeys = new Set<string>();

  matchedNodes.forEach((matchedNode) => {
    const visitedKeys = new Set<string>();
    let parentNode = data.find(
      (candidate) =>
        candidate.nodeId === matchedNode.parentNodeId &&
        candidate.sourceId === matchedNode.parentSourceId,
    );

    while (parentNode) {
      const parentKey = getNodeKey(parentNode);
      if (visitedKeys.has(parentKey)) {
        break;
      }
      visitedKeys.add(parentKey);
      expandedKeys.add(parentKey);

      if (parentNode.nodeId === ROOT_NODE_ID) {
        break;
      }
      const currentParentNode = parentNode;
      parentNode = data.find(
        (candidate) =>
          candidate.nodeId === currentParentNode.parentNodeId &&
          candidate.sourceId === currentParentNode.parentSourceId,
      );
    }
  });

  return expandedKeys;
};

const formatValue = (value: number | null | undefined) =>
  value == null
    ? '—'
    : Math.round(value).toLocaleString('en-GB', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

type EpochValue = number | string;

const parseEpochMillis = (value?: EpochValue) => {
  if (value == null) {
    return null;
  }
  const parsedValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isShipperPairNode = (nodeId: number) => nodeId === SHIPPER_PAIR_NODE_ID;
// The jobs action is only meaningful at the Contract level of this hierarchy.
const canShowJobs = (nodeId: number) => nodeId === CONTRACT_NODE_ID;

type HierarchyLevel = 'market' | 'contract' | 'position' | 'shipper';

const hierarchyLevelByNodeId: Record<number, HierarchyLevel> = {
  [ROOT_NODE_ID]: 'market',
  [CONTRACT_NODE_ID]: 'contract',
  [POSITION_NODE_ID]: 'position',
  [SHIPPER_PAIR_NODE_ID]: 'shipper',
};

const hierarchyLevelDetails: Record<HierarchyLevel, { shortLabel: string; label: string }> = {
  market: { shortLabel: 'M', label: 'Market' },
  contract: { shortLabel: 'C', label: 'Contract' },
  position: { shortLabel: 'P', label: 'Position' },
  shipper: { shortLabel: 'S', label: 'Shipper Code Pair' },
};

const MARKET_FLAG_SIZE = 20;
const ADDITIONAL_MARKET_FLAG_CODES: Record<string, string> = {
  'Czech Republic': 'Cz',
  'United Kingdom': 'Gb',
  Ireland: 'Ie',
  Poland: 'Pl',
};

const getMarketFlagCode = (marketName: string) =>
  ADDITIONAL_MARKET_FLAG_CODES[marketName.trim()] ??
  COUNTRY_HEALTH_DATA.find(
    (country) => country.name.toLocaleLowerCase() === marketName.trim().toLocaleLowerCase(),
  )?.flagCode;

const HierarchyBadge = ({ level, marketName }: { level: HierarchyLevel; marketName?: string }) => {
  const details = hierarchyLevelDetails[level];
  const flagCode = level === 'market' && marketName ? getMarketFlagCode(marketName) : undefined;
  const FlagComponent = flagCode
    ? (Flags as Record<string, React.FC<IFlagProps>>)[flagCode]
    : undefined;

  return (
    <span
      className={`hierarchy-badge hierarchy-badge-${level}`}
      aria-label={details.label}
      title={details.label}
    >
      {FlagComponent ? (
        <FlagComponent height={MARKET_FLAG_SIZE} width={MARKET_FLAG_SIZE} />
      ) : (
        details.shortLabel
      )}
    </span>
  );
};

const getDealLabel = (deal: IMarketPositionDeal, index: number) => {
  const label = [
    deal.name,
    deal.dealDealLegId,
    deal.dealLegId,
    deal.deal_DealLegId,
    deal.dealId,
    deal.id,
  ].find((value) => value != null);
  return label == null ? `Deal ${index + NUMBERS.ONE}` : String(label);
};

const getExpandIcon = (canExpand: boolean, expanded: boolean) => {
  if (!canExpand) {
    return null;
  }
  return expanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />;
};

type DealRowProps = {
  deal: IMarketPositionDeal;
  positionKey: string;
  index: number;
  columns: PeriodColumn[];
  showSelection: boolean;
};

const DealRow = ({ deal, positionKey, index, columns, showSelection }: DealRowProps) => {
  const dealLabel = getDealLabel(deal, index);
  const dealKey = `${positionKey}:deal:${dealLabel}`;
  const startEpoch = parseEpochMillis(deal.periodStartUtcEpoch ?? undefined);
  const endEpoch = parseEpochMillis(deal.periodEndUtcEpoch ?? undefined);
  const columnValues = columns.map((column) =>
    getDealColumnValue(column, startEpoch, endEpoch, deal.quantity),
  );
  const dealTotal = columnValues.reduce((sum, value) => sum + value, ZERO);

  return (
    <tr key={dealKey} data-testid={`contract-view-new-deal-${dealKey}`}>
      <td className='tree-column'>
        <div className='tree-cell' style={{ paddingLeft: `${DEAL_INDENT_PX}px` }}>
          <span title={dealLabel} aria-label={dealLabel}>
            {dealLabel}
          </span>
        </div>
      </td>
      <td className='volume-type-column'>TRADE</td>
      {showSelection && <td className='selection-column' />}
      <td className='total-column'>{formatValue(dealTotal)}</td>
      {columns.map((column, columnIndex) => (
        <td key={`${dealKey}-${column.key}`}>{formatValue(columnValues[columnIndex])}</td>
      ))}
    </tr>
  );
};

const ContractViewNew = () => {
  const dispatch = useAppDispatch();
  const { nodes, dealsByPositionId, expandedKeys } = useAppSelector(
    (state) => state.ContractViewNew,
  );
  const {
    isCreating,
    startCreating,
    cancelCreating,
    getSelectionState,
    toggleSelection,
    hasSelectedNodes,
    selectedMappings,
  } = useCustomViewSelection(nodes);
  // Keep the request version paired with its view, even if clearing Redux triggers a render.
  const [{ customViewId, requestVersion }, setCustomView] = useState<{
    customViewId: number | null;
    requestVersion: number;
  }>({ customViewId: null, requestVersion: 0 });
  const viewRequestVersion = useRef(0);
  const [period, setPeriod] = useState<PositionPeriod>(DEFAULT_PERIOD);
  const [gasDay, setGasDay] = useState(() =>
    dayjs().tz(CET_TIME_ZONE).format(COMMON_MESSAGES.DATE_YYYY_MM_DD),
  );
  const { fromDate, toDate } = getPeriodRange(gasDay, period);
  const periodColumns = useMemo(() => getPeriodColumns(gasDay, period), [gasDay, period]);
  const tableColumnCount = FIXED_COLUMN_COUNT + periodColumns.length + Number(isCreating);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTableFocused, setIsTableFocused] = useState(false);
  const [loadingNodeKey, setLoadingNodeKey] = useState<string | null>(null);
  const [selectedMarketNames, setSelectedMarketNames] = useState<string[]>([]);
  const [loadedMarketSourceIds, setLoadedMarketSourceIds] = useState<Set<number>>(new Set());
  const [searchInputText, setSearchInputText] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>([]);
  const [selectedContractTypes, setSelectedContractTypes] = useState<string[]>([]);
  const appliedSearchFilters = useRef<{ organizationIds: string[]; contractTypes: string[] }>({
    organizationIds: [],
    contractTypes: [],
  });
  const [isSearchActive, setIsSearchActive] = useState(false);
  const isSearchActiveRef = useRef(isSearchActive);
  isSearchActiveRef.current = isSearchActive;
  const [isSearchValidationPopupOpen, setIsSearchValidationPopupOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    node: IMarketPositionNode;
    top: number;
    left: number;
  } | null>(null);
  const [jobsModal, setJobsModal] = useState<{
    node: IMarketPositionNode | null;
    jobs: JobRecord[];
    isLoading: boolean;
  }>({ node: null, jobs: [], isLoading: false });
  const expandedKeySet = useMemo(() => new Set(expandedKeys), [expandedKeys]);
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  const handleCustomViewChange = (id: number | null, forceReload = false) => {
    if (id === customViewId && !forceReload) {
      return;
    }
    viewRequestVersion.current += 1;
    dispatch(clear());
    setLoadedMarketSourceIds(new Set());
    setLoadingNodeKey(null);
    setCustomView({ customViewId: id, requestVersion: viewRequestVersion.current });
  };

  // A new date range invalidates in-flight responses, which may belong to a different column set.
  const changeDateRange = (nextGasDay: string, nextPeriod: PositionPeriod) => {
    const nextRange = getPeriodRange(nextGasDay, nextPeriod);
    const rangeChanged =
      nextPeriod !== period || nextRange.fromDate !== fromDate || nextRange.toDate !== toDate;
    setGasDay(nextGasDay);
    if (!rangeChanged) {
      return;
    }
    viewRequestVersion.current += 1;
    const nextRequestVersion = viewRequestVersion.current;
    setCustomView((previous) => ({ ...previous, requestVersion: nextRequestVersion }));
    setLoadingNodeKey(null);
    setPeriod(nextPeriod);
  };

  const fetchNodes = useCallback(
    async (nodesToFetch: number[], filterNodeId?: number, filterSourceIds: number[] = []) => {
      const query = new URLSearchParams({
        fromDate,
        toDate,
        nodesToFetch: nodesToFetch.join(','),
        filterByNodeId: filterNodeId == null ? '' : String(filterNodeId),
        filterBySourceIds: filterSourceIds.join(','),
      });
      if (customViewId !== null) {
        query.set('customViewId', String(customViewId));
      }
      const endpoint = getPeriodEndpoint(GET_CONTRACT_BY_PERIOD, period);
      const response = await get(`${endpoint}?${query.toString()}`);
      if (requestVersion !== viewRequestVersion.current) {
        return [];
      }
      const data = (response.data ?? []) as IMarketPositionNode[];
      dispatch(mergeNodes(data));
      return data;
    },
    [dispatch, fromDate, toDate, period, customViewId, requestVersion],
  );

  const fetchDeals = useCallback(
    async (positionId: number) => {
      const dateParams: Record<string, string> =
        period === DEFAULT_PERIOD ? { gasDay: fromDate } : { fromDate, toDate };
      const query = new URLSearchParams({
        ...dateParams,
        positionIds: String(positionId),
      });
      const response = await get(`${GET_MARKET_POSITION_DEALS}?${query.toString()}`);
      if (requestVersion !== viewRequestVersion.current) {
        return;
      }
      dispatch(
        setPositionDeals({
          positionId,
          deals: (response.data ?? []) as IMarketPositionDeal[],
        }),
      );
    },
    [dispatch, fromDate, toDate, period, requestVersion],
  );

  const fetchSearchResults = useCallback(
    async (trimmedSearchText: string) => {
      const query = new URLSearchParams({
        fromDate,
        toDate,
        searchText: trimmedSearchText,
        organizationIds: appliedSearchFilters.current.organizationIds.join(','),
        type: appliedSearchFilters.current.contractTypes.join(','),
      });
      if (customViewId !== null) {
        query.set('customViewId', String(customViewId));
      }
      const endpoint = getPeriodEndpoint(SEARCH_CONTRACT_BY_PERIOD, period);
      const response = await get(`${endpoint}?${query.toString()}`);
      return (response.data ?? []) as IMarketPositionNode[];
    },
    [fromDate, toDate, period, customViewId],
  );

  // Search results replace the store outright; only expansion afterwards appends missing data.
  const applySearchResults = (data: IMarketPositionNode[], activeSearchText: string) => {
    if (requestVersion !== viewRequestVersion.current) {
      return;
    }
    dispatch(clear());
    dispatch(mergeNodes(data));
    const searchedMarketSourceIds = data
      .filter((node) => node.nodeId === ROOT_NODE_ID && node.volumeType === CALC_VOLUME_TYPE)
      .map((node) => node.sourceId);
    setLoadedMarketSourceIds(new Set(searchedMarketSourceIds));
    // Expand ancestors of every name match so matching rows are visible without manual expanding.
    dispatch(setExpandedKeys(Array.from(computeSearchExpandedKeys(data, activeSearchText))));
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInputText(event.target.value);
  };

  const reloadSearch = (text: string, organizationIds: string[], contractTypes: string[]) => {
    appliedSearchFilters.current = {
      organizationIds: [...organizationIds],
      contractTypes: [...contractTypes],
    };
    searchTextRef.current = text;
    isSearchActiveRef.current = Boolean(text || organizationIds.length || contractTypes.length);
    setSearchText(text);
    setIsSearchActive(isSearchActiveRef.current);
    viewRequestVersion.current += 1;
    setLoadingNodeKey(null);
    setCustomView((previous) => ({ ...previous, requestVersion: viewRequestVersion.current }));
  };

  const handleOrganizationIdsChange = (ids: string[]) => {
    setSelectedOrganizationIds(ids);
    reloadSearch(searchText, ids, selectedContractTypes);
  };

  const handleContractTypesChange = (types: string[]) => {
    setSelectedContractTypes(types);
    reloadSearch(searchText, selectedOrganizationIds, types);
  };

  const handleSearchApply = () => {
    const trimmedSearchText = searchInputText.trim();
    if (trimmedSearchText.length < MIN_SEARCH_CHARACTERS) {
      setIsSearchValidationPopupOpen(true);
      return;
    }

    reloadSearch(trimmedSearchText, selectedOrganizationIds, selectedContractTypes);
  };

  const handleSearchClear = () => {
    setSearchInputText('');
    reloadSearch('', selectedOrganizationIds, selectedContractTypes);
  };

  useEffect(() => {
    let active = true;
    dispatch(clear());
    setIsLoading(true);

    // A gas day change re-runs whichever data source is currently active: search or the plain hierarchy.
    const activeSearchText = searchTextRef.current;
    const loadData = isSearchActiveRef.current
      ? fetchSearchResults(activeSearchText).then((data) => {
          if (active) {
            applySearchResults(data, activeSearchText);
          }
        })
      : fetchNodes([ROOT_NODE_ID]).then(() => undefined);

    loadData
      .catch(() => {
        if (active) {
          sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
      dispatch(clear());
      setLoadedMarketSourceIds(new Set());
    };
  }, [dispatch, fetchNodes, fetchSearchResults]);

  // Close the row context menu on any outside click/scroll/Escape, since it is fully self-managed.
  useEffect(() => {
    if (contextMenu == null) {
      return undefined;
    }
    const closeMenu = () => setContextMenu(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };
    document.addEventListener('click', closeMenu);
    document.addEventListener('scroll', closeMenu, true);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('scroll', closeMenu, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [contextMenu]);

  const childrenFor = useCallback(
    (parent: IMarketPositionNode) =>
      nodes.filter(
        (node) =>
          node.volumeType === CALC_VOLUME_TYPE &&
          node.parentNodeId === parent.nodeId &&
          node.parentSourceId === parent.sourceId,
      ),
    [nodes],
  );

  const volumeTypesFor = useCallback(
    (node: IMarketPositionNode) =>
      nodes.filter((candidate) => {
        if (candidate.volumeType === 'MANUAL' && candidate.total === null) {
          return false;
        }
        return (
          candidate.nodeId === node.nodeId &&
          candidate.sourceId === node.sourceId &&
          candidate.volumeType !== CALC_VOLUME_TYPE
        );
      }),
    [nodes],
  );

  const findMarketSourceId = useCallback(
    (node: IMarketPositionNode) => {
      let currentNode: IMarketPositionNode | undefined = node;
      const visitedKeys = new Set<string>();

      while (currentNode) {
        const currentKey = getNodeKey(currentNode);
        if (visitedKeys.has(currentKey)) {
          return undefined;
        }
        visitedKeys.add(currentKey);

        if (currentNode.nodeId === ROOT_NODE_ID) {
          return currentNode.sourceId;
        }
        currentNode = nodes.find(
          (candidate) =>
            candidate.nodeId === currentNode?.parentNodeId &&
            candidate.sourceId === currentNode?.parentSourceId,
        );
      }

      return undefined;
    },
    [nodes],
  );

  const toggleNode = async (node: IMarketPositionNode) => {
    const key = getNodeKey(node);
    if (expandedKeySet.has(key)) {
      dispatch(setExpandedKeys(expandedKeys.filter((expandedKey) => expandedKey !== key)));
      return;
    }

    dispatch(setExpandedKeys([...expandedKeys, key]));

    // Deals remain filtered by the pair's parent position.
    if (isShipperPairNode(node.nodeId)) {
      setLoadingNodeKey(key);
      try {
        await fetchDeals(node.parentSourceId);
      } catch {
        dispatch(setExpandedKeys(expandedKeys));
        sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
      } finally {
        setLoadingNodeKey(null);
      }
      return;
    }

    // Sub-hierarchy nodes are already loaded once their market is fetched.
    if (node.nodeId !== ROOT_NODE_ID || loadedMarketSourceIds.has(node.sourceId)) {
      return;
    }

    // Expanding a market fetches all of its hierarchy in a single call (nodesToFetch = null).
    setLoadingNodeKey(key);
    try {
      await fetchNodes([], ROOT_NODE_ID, [node.sourceId]);
      setLoadedMarketSourceIds((previousIds) => new Set(previousIds).add(node.sourceId));
    } catch {
      dispatch(setExpandedKeys(expandedKeys));
      sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
    } finally {
      setLoadingNodeKey(null);
    }
  };

  const refreshExpandedHierarchy = async () => {
    // Refreshing while a search is active should re-run the search rather than the plain hierarchy fetch.
    if (isSearchActive) {
      setIsRefreshing(true);
      try {
        const trimmedSearchText = searchText.trim();
        const data = await fetchSearchResults(trimmedSearchText);
        applySearchResults(data, trimmedSearchText);
      } catch {
        sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
      } finally {
        setIsRefreshing(false);
      }
      return;
    }

    const expandedMarketSourceIds = Array.from(
      new Set(
        nodes
          .filter(
            (node) =>
              node.nodeId === ROOT_NODE_ID &&
              node.volumeType === CALC_VOLUME_TYPE &&
              expandedKeySet.has(getNodeKey(node)),
          )
          .map((node) => node.sourceId),
      ),
    );

    if (expandedMarketSourceIds.length === ZERO) {
      return;
    }

    setIsRefreshing(true);
    try {
      await fetchNodes([], ROOT_NODE_ID, expandedMarketSourceIds);
      setLoadedMarketSourceIds((previousIds) => {
        const nextIds = new Set(previousIds);
        expandedMarketSourceIds.forEach((sourceId) => nextIds.add(sourceId));
        return nextIds;
      });
    } catch {
      sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Leave shipper pairs collapsed so deals are only loaded on demand.
  const collectDescendantKeys = useCallback(
    (node: IMarketPositionNode, source: IMarketPositionNode[]) => {
      const keys: string[] = [];
      const queue: IMarketPositionNode[] = [node];
      while (queue.length > ZERO) {
        const current = queue.shift() as IMarketPositionNode;
        const children = source.filter(
          (candidate) =>
            candidate.volumeType === CALC_VOLUME_TYPE &&
            candidate.nodeId !== SHIPPER_PAIR_NODE_ID &&
            candidate.parentNodeId === current.nodeId &&
            candidate.parentSourceId === current.sourceId,
        );
        for (const child of children) {
          keys.push(getNodeKey(child));
          queue.push(child);
        }
      }
      return keys;
    },
    [],
  );

  const expandAllUnder = async (node: IMarketPositionNode) => {
    const key = getNodeKey(node);
    let source = nodes;

    if (node.nodeId === ROOT_NODE_ID && !loadedMarketSourceIds.has(node.sourceId)) {
      // Mark expanded immediately so the "Loading..." row shows while data is fetched.
      setLoadingNodeKey(key);
      dispatch(setExpandedKeys(Array.from(new Set([...expandedKeys, key]))));
      try {
        source = await fetchNodes([], ROOT_NODE_ID, [node.sourceId]);
        if (requestVersion !== viewRequestVersion.current) {
          return;
        }
        setLoadedMarketSourceIds((previousIds) => new Set(previousIds).add(node.sourceId));
      } catch {
        sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
        setLoadingNodeKey(null);
        return;
      }
      setLoadingNodeKey(null);
    }

    const nextKeys = new Set([...expandedKeys, key, ...collectDescendantKeys(node, source)]);
    dispatch(setExpandedKeys(Array.from(nextKeys)));
  };

  const renderRowMenu = (node: IMarketPositionNode) => (
    <div className='menu-container'>
      <Button
        size='small'
        variant='transparent'
        data-testid={`contract-view-new-expand-all-${node.nodeId}-${node.sourceId}`}
        onClick={() => {
          setContextMenu(null);
          void expandAllUnder(node);
        }}
      >
        Expand all
      </Button>
      {canShowJobs(node.nodeId) && (
        <Button
          size='small'
          variant='transparent'
          data-testid={`contract-view-new-show-jobs-${node.nodeId}-${node.sourceId}`}
          onClick={() => void openJobsModal(node)}
        >
          Show send and receive jobs
        </Button>
      )}
    </div>
  );

  const renderNoDealsRow = (positionKey: string) => (
    <tr key={`${positionKey}:no-deals`} data-testid='contract-view-new-no-deals'>
      <td className='tree-column' colSpan={tableColumnCount}>
        <div className='tree-cell tree-loading-cell' style={{ paddingLeft: `${DEAL_INDENT_PX}px` }}>
          No deals found.
        </div>
      </td>
    </tr>
  );

  const renderNoRecordsRow = (parentKey: string, depth: number) => (
    <tr key={`${parentKey}:no-records`} data-testid='contract-view-new-no-records'>
      <td className='tree-column' colSpan={tableColumnCount}>
        <div
          className='tree-cell tree-loading-cell'
          style={{
            paddingLeft: `${(depth + 1) * TREE_INDENT_STEP_PX + LOADING_TOGGLE_OFFSET_PX}px`,
          }}
        >
          No records found.
        </div>
      </td>
    </tr>
  );

  const renderVolumeTypeRow = (volumeTypeNode: IMarketPositionNode, parentKey: string) => {
    const volumeTypeKey = `${parentKey}:volumeType:${volumeTypeNode.volumeTypeId}`;
    const selectionState = getSelectionState(volumeTypeNode);
    return (
      <tr
        key={volumeTypeKey}
        data-testid={`contract-view-new-volume-type-${volumeTypeNode.nodeId}-${volumeTypeNode.sourceId}-${volumeTypeNode.volumeType}`}
      >
        <td className='tree-column' />
        <td className='volume-type-column'>{volumeTypeNode.volumeType}</td>
        {isCreating && (
          <td className='selection-column'>
            <Checkbox
              size={Sizes.Small}
              aria-label={`Select ${volumeTypeNode.name} ${volumeTypeNode.volumeType}`}
              checked={selectionState.checked}
              indeterminate={selectionState.indeterminate}
              aria-checked={selectionState.indeterminate ? 'mixed' : selectionState.checked}
              onChange={() => toggleSelection(volumeTypeNode)}
              className='selection-checkbox'
            />
          </td>
        )}
        <td className='total-column'>{formatValue(volumeTypeNode.total)}</td>
        {periodColumns.map((column) => (
          <td key={`${volumeTypeKey}-${column.key}`}>{formatValue(volumeTypeNode[column.key])}</td>
        ))}
      </tr>
    );
  };

  const getNameRect = (element: HTMLElement) => element.getBoundingClientRect();

  const openContextMenuFor = (node: IMarketPositionNode, element: HTMLElement) => {
    const nameRect = getNameRect(element);
    setContextMenu({ node, top: nameRect.bottom, left: nameRect.right });
  };

  const openJobsModal = async (node: IMarketPositionNode) => {
    setContextMenu(null);
    setJobsModal({ node, jobs: [], isLoading: true });
    try {
      const query = new URLSearchParams({ masterDataId: String(node.id) });
      const response = await get(`${GET_JOBS}?${query.toString()}`);
      const data = response.data;
      let jobs: JobRecord[] = [];
      if (Array.isArray(data)) {
        jobs = data;
      } else if (Array.isArray(data?.items)) {
        jobs = data.items;
      }
      setJobsModal({ node, jobs, isLoading: false });
    } catch {
      sendNotification({ type: 'error', message: COMMON_MESSAGES.ERROR });
      setJobsModal({ node, jobs: [], isLoading: false });
    }
  };

  const isMenuShortcutKey = (event: React.KeyboardEvent) =>
    event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');

  const isContextMenuKey = (event: React.KeyboardEvent) =>
    event.key === 'Enter' || event.key === ' ' || isMenuShortcutKey(event);

  const renderNodeName = (node: IMarketPositionNode, canExpandAll: boolean) => {
    const level = hierarchyLevelByNodeId[node.nodeId];
    const name = (
      <span className='tree-node-name' title={node.name} aria-label={node.name}>
        {level ? <HierarchyBadge level={level} marketName={node.name} /> : null}
        <span className='tree-node-label' title={node.name}>
          {node.name}
        </span>
      </span>
    );
    if (!canExpandAll) {
      return name;
    }
    return (
      <button
        type='button'
        className='tree-cell-context-target'
        title={node.name}
        aria-haspopup='menu'
        aria-label={`Show actions for ${node.name}`}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          openContextMenuFor(node, event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (!isContextMenuKey(event)) {
            return;
          }
          event.preventDefault();
          openContextMenuFor(node, event.currentTarget);
        }}
      >
        {name}
      </button>
    );
  };

  const renderShipperPairDescendantRows = (
    node: IMarketPositionNode,
    key: string,
    loadingRow: React.ReactNode,
  ) => {
    const deals = dealsByPositionId[String(node.parentSourceId)];
    if (loadingRow != null) {
      return [];
    }
    if (deals != null && deals.length > ZERO) {
      return deals.map((deal, index) => (
        <DealRow
          key={`${key}:deal:${index}`}
          deal={deal}
          positionKey={key}
          index={index}
          columns={periodColumns}
          showSelection={isCreating}
        />
      ));
    }
    return [renderNoDealsRow(key)];
  };

  const renderNode = (node: IMarketPositionNode, depth: number): React.ReactNode[] => {
    const key = getNodeKey(node);
    const selectionState = getSelectionState(node);
    const children = childrenFor(node);
    const expanded = expandedKeySet.has(key);
    const canExpand = LEVEL_AFTER_NODE[node.nodeId] != null || isShipperPairNode(node.nodeId);
    const canExpandAll = LEVEL_AFTER_NODE[node.nodeId] != null;
    const loadingRow =
      loadingNodeKey === key ? (
        <tr
          key={`${key}:loading`}
          data-testid={`contract-view-new-loading-${node.nodeId}-${node.sourceId}`}
        >
          <td className='tree-column' colSpan={tableColumnCount}>
            <div
              className='tree-cell tree-loading-cell'
              style={{
                paddingLeft: `${(depth + 1) * TREE_INDENT_STEP_PX + LOADING_TOGGLE_OFFSET_PX}px`,
              }}
            >
              Loading...
            </div>
          </td>
        </tr>
      ) : null;
    const treeCell = (
      <div className='tree-cell' style={{ paddingLeft: `${depth * TREE_INDENT_STEP_PX}px` }}>
        <button
          type='button'
          className='tree-toggle'
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.name}`}
          disabled={!canExpand || loadingNodeKey === key}
          onClick={() => void toggleNode(node)}
        >
          {getExpandIcon(canExpand, expanded)}
        </button>
        {renderNodeName(node, canExpandAll)}
      </div>
    );
    const row = (
      <tr
        key={key}
        className={node.nodeId === ROOT_NODE_ID ? 'market-header-row' : undefined}
        data-testid={`contract-view-new-row-${node.nodeId}-${node.sourceId}`}
      >
        <td className='tree-column'>{treeCell}</td>
        <td className='volume-type-column'>{node.volumeType}</td>
        {isCreating && (
          <td className='selection-column'>
            <Checkbox
              size={Sizes.Small}
              aria-label={`Select ${node.name}`}
              checked={selectionState.checked}
              indeterminate={selectionState.indeterminate}
              aria-checked={selectionState.indeterminate ? 'mixed' : selectionState.checked}
              onChange={() => toggleSelection(node)}
              className='selection-checkbox'
            />
          </td>
        )}
        <td className='total-column'>{formatValue(node.total)}</td>
        {periodColumns.map((column) => (
          <td key={`${key}-${column.key}`} data-testid={`${key}-${column.key}`}>
            {formatValue(node[column.key])}
          </td>
        ))}
      </tr>
    );

    if (!expanded) {
      return [row];
    }
    const volumeTypeRows = volumeTypesFor(node).map((volumeTypeNode) =>
      renderVolumeTypeRow(volumeTypeNode, key),
    );
    const loadingRows = loadingRow ? [loadingRow] : [];

    if (isShipperPairNode(node.nodeId)) {
      const dealRows = renderShipperPairDescendantRows(node, key, loadingRow);
      return [row, ...loadingRows, ...volumeTypeRows, ...dealRows];
    }

    const marketSourceId = findMarketSourceId(node);
    const isMarketLoaded = marketSourceId != null && loadedMarketSourceIds.has(marketSourceId);
    const noRecordsRows =
      isMarketLoaded && children.length === ZERO ? [renderNoRecordsRow(key, depth)] : [];
    return [
      row,
      ...loadingRows,
      ...volumeTypeRows,
      ...noRecordsRows,
      ...children.flatMap((child) => renderNode(child, depth + 1)),
    ];
  };

  const rootNodes = nodes.filter(
    (node) => node.nodeId === ROOT_NODE_ID && node.volumeType === CALC_VOLUME_TYPE,
  );
  const visibleRootNodes =
    selectedMarketNames.length > 0
      ? rootNodes.filter((node) => selectedMarketNames.includes(node.name))
      : rootNodes;
  const rows = visibleRootNodes.flatMap((node) => renderNode(node, 0));

  return (
    <ContractViewNewContainer className={isTableFocused ? 'table-focused' : undefined}>
      <div className='filter-row'>
        <div className='filter-field period-filter-field'>
          <span className='filter-label'>Period</span>
          <Select
            size={Sizes.Small}
            aria-label='Period'
            options={PERIOD_OPTIONS}
            optionLabelProp='label'
            showSearch={false}
            value={period}
            allowClear={false}
            onChange={(value) => changeDateRange(gasDay, value as PositionPeriod)}
            data-testid='contract-view-new-period-select'
          />
        </div>
        <div className='filter-field'>
          <div className='gas-day-controls'>
            <Button
              onClick={() =>
                changeDateRange(
                  dayjs(gasDay).subtract(1, 'day').format(COMMON_MESSAGES.DATE_YYYY_MM_DD),
                  period,
                )
              }
              icon={<Icons.ChevronLeft />}
              iconOnly
              size='small'
              variant='outlined'
              aria-label='Previous gas day'
              data-testid='contract-view-new-gasday-previous'
            />
            <div className='gas-day-picker'>
              <span className='filter-label'>Gas Day</span>
              <SingleDatePicker
                className='gas-day'
                size={Sizes.Small}
                format='DD MMM YYYY'
                allowClear={false}
                value={dayjs(gasDay)}
                onChange={(value) =>
                  changeDateRange(dayjs(value).format(COMMON_MESSAGES.DATE_YYYY_MM_DD), period)
                }
                data-testid='contract-view-new-gasday'
              />
            </div>
            <Button
              onClick={() =>
                changeDateRange(
                  dayjs(gasDay).add(1, 'day').format(COMMON_MESSAGES.DATE_YYYY_MM_DD),
                  period,
                )
              }
              icon={<Icons.ChevronRight />}
              iconOnly
              size='small'
              variant='outlined'
              aria-label='Next gas day'
              data-testid='contract-view-new-gasday-next'
            />
          </div>
        </div>
        <PositionSearchFilters
          organizationIds={selectedOrganizationIds}
          onOrganizationIdsChange={handleOrganizationIdsChange}
          contractTypes={selectedContractTypes}
          onContractTypesChange={handleContractTypesChange}
        />
        <div className='search-container'>
          <span className='filter-label'>Search</span>
          <div className='search-input-row'>
            <TextInput
              size={Sizes.Small}
              placeholder='Search…'
              suffix={{
                node: (
                  <Button
                    className='search-apply-btn'
                    iconOnly
                    icon={<Icons.Search height={SEARCH_ICON_SIZE} width={SEARCH_ICON_SIZE} />}
                    onClick={handleSearchApply}
                    aria-label='Apply search'
                  />
                ),
              }}
              value={searchInputText}
              onChange={handleSearchInputChange}
              data-testid='contract-view-new-search'
            />
            {searchText ? (
              <Button
                size='small'
                className='search-clear-btn'
                onClick={handleSearchClear}
                data-testid='contract-view-new-search-clear'
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
        <Button
          size='small'
          className='market-refresh-button'
          iconOnly
          icon={<Icons.ArrowCcw height={REFRESH_ICON_SIZE} width={REFRESH_ICON_SIZE} />}
          onClick={() => void refreshExpandedHierarchy()}
          loading={isRefreshing}
          disabled={isLoading || isRefreshing}
          data-testid='contract-view-new-refresh'
          style={{ marginTop: BUTTON_MARGIN_TOP }}
          aria-label='Refresh contract view'
        />
        <CustomViewSelect
          timeSeriesView={TIME_SERIES_VIEW_IDS.CONTRACT}
          onCreateNew={startCreating}
          onEdit={() => startCreating(true)}
          onCancelCreate={cancelCreating}
          hasSelectedNodes={hasSelectedNodes}
          selectedMappings={selectedMappings}
          onSaved={cancelCreating}
          onViewChange={handleCustomViewChange}
        />
      </div>
      <MarketButtonGroup selectedMarkets={selectedMarketNames} onChange={setSelectedMarketNames} />
      <div className='table-area'>
        {!isLoading && !isRefreshing ? (
          <TableFocusToggle
            focused={isTableFocused}
            onToggle={() => setIsTableFocused((focused) => !focused)}
          />
        ) : null}
        {!isLoading && !isRefreshing && rows.length === 0 ? (
          <p className='empty-message'>No contract view data available.</p>
        ) : null}
        {isLoading || isRefreshing ? (
          <output aria-label='Loading contract view' data-testid='contract-view-new-table-loading'>
            <Loader />
          </output>
        ) : null}
        {!isLoading && !isRefreshing && rows.length > 0 ? (
          <div className='time-series-wrapper'>
            <table className='time-series-table'>
              <thead>
                <tr>
                  <th className='tree-column' />
                  <th className='volume-type-column'>Volume Type</th>
                  {isCreating && <th className='selection-column' aria-label='Selection' />}
                  <th className='total-column'>Total</th>
                  {periodColumns.map((column) => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </table>
          </div>
        ) : null}
      </div>
      {contextMenu == null ? null : (
        <ActionMenu
          $top={contextMenu.top}
          $left={contextMenu.left}
          data-testid='contract-view-new-context-menu'
        >
          {renderRowMenu(contextMenu.node)}
        </ActionMenu>
      )}
      <SearchValidationModal
        open={isSearchValidationPopupOpen}
        closable={false}
        isDraggable
        width={400}
        footer={
          <div className='button-style'>
            <Button
              onClick={() => setIsSearchValidationPopupOpen(false)}
              variant='outlined'
              data-testid='contract-view-new-search-validation-close'
            >
              Close
            </Button>
          </div>
        }
        onClose={() => setIsSearchValidationPopupOpen(false)}
        showHeader={false}
        maskClosable={false}
        style={{ top: '30%' }}
        data-testid='contract-view-new-search-validation-modal'
      >
        {SEARCH_VALIDATION_MESSAGE}
      </SearchValidationModal>
      <Jobs
        open={jobsModal.node !== null}
        nodeName={jobsModal.node?.name}
        jobs={jobsModal.jobs}
        isLoading={jobsModal.isLoading}
        masterDataId={jobsModal.node?.id}
        onClose={() => setJobsModal({ node: null, jobs: [], isLoading: false })}
        onJobSaved={() => {
          if (jobsModal.node) {
            void openJobsModal(jobsModal.node);
          }
        }}
      />
    </ContractViewNewContainer>
  );
};

export default ContractViewNew;
